import {Component} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {UsersService} from "../../services/users.service";
import {IUpdateAdminPasswordRequest} from "../../interfaces/IUpdateAdminPasswordRequest";
import {OidcService} from "../../services/oidc.service";
import {SnackbarService} from "../../services/snackbar.service";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {IApiErrorResponse} from "../../interfaces/error/IApiErrorResponse";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {MatCardModule} from "@angular/material/card";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {AppConfigService} from "../../services/app-config.service";

// Mirrors the relevant values in Causable.Api.Data.Enum.ErrorTypes (there is no shared front-end error-type enum).
const UPDATE_PASSWORD_ERROR = {
  OldPasswordDoesntMatch: 67,
  MatchesRecentPassword: 193
} as const;

@Component({
  selector: 'app-update-password',
  standalone: true,
  templateUrl: './update-password.component.html',
  imports: [
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInput,
    MatIcon,
    MatButton
  ],
  styleUrls: ['./update-password.component.scss']
})
export class UpdatePasswordComponent {

  public uiStateEnum: UIStateEnum = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;
  public errorMessages: string[] = [];
  public showOldPassword: boolean = false;
  public showNewPassword: boolean = false;
  public showConfirmPassword: boolean = false;
  public minimumPasswordLength: number = this.appConfigService.getMinimumPasswordLength();

  public newPasswordControl: FormControl = new FormControl<string>('', [
    Validators.required,
    Validators.maxLength(50),
    Validators.minLength(this.minimumPasswordLength),
    Validators.pattern(`^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{${this.minimumPasswordLength},}$`)
  ]);

  public confirmNewPasswordControl: FormControl = new FormControl<string>('', [
    Validators.required,
    Validators.pattern(this.newPasswordControl.value)
  ]);

  public oldPasswordControl: FormControl = new FormControl<string>('', [
    Validators.required
  ]);

  public updatePasswordForm: FormGroup = this.formBuilder.group({
      oldPassword: this.oldPasswordControl,
      newPassword: this.newPasswordControl,
      confirmNewPassword: this.confirmNewPasswordControl
    },
    {
      validators: this.confirmedValidator('newPassword', 'confirmNewPassword')
    })

  constructor(private userService: UsersService,
              private formBuilder: FormBuilder,
              private snackBarService: SnackbarService,
              private errorHandlingService: ErrorHandlingService,
              private oidcService: OidcService,
              private appConfigService: AppConfigService) {
  }

  updatePassword() {
    this.errorHandlingService.clearErrorMessages();
    if (!this.updatePasswordForm.valid) {
      this.updatePasswordForm.markAllAsTouched()
      return;
    }

    const adminId = this.oidcService.getAdminIdFromClaims();

    if (adminId) {
      this.errorMessages = [];
      this.uiStateEnum = UIStateEnum.ShowLoading;
      const updatePassword: IUpdateAdminPasswordRequest = {
        oldPassword: this.oldPasswordControl.value,
        newPassword: this.newPasswordControl.value,
        adminId: adminId
      }
      this.userService.updateAdminPassword(updatePassword).subscribe({
        next: () => {
          this.uiStateEnum = UIStateEnum.ShowData;
          this.snackBarService.openSuccessfulSnackBar('Password updated successfully!')
        },
        error: (res: IApiErrorResponse) => {
          this.uiStateEnum = UIStateEnum.ShowData;
          const message = this.resolveUpdatePasswordErrorMessage(res);
          this.errorMessages = message ? [message] : [];
          this.snackBarService.openErrorSnackBar(message || 'Something went wrong updating your password. Please try again.');
        }
      })
    }
  }

  // UpdateAdminPassword returns an ErrorType only (ClientErrorMessages is deprecated/empty), so map the actionable ones
  // to specific copy.
  private resolveUpdatePasswordErrorMessage(res: IApiErrorResponse): string {
    if (res.Error?.ClientErrorMessages?.length > 0) {
      return res.Error.ClientErrorMessages.toString();
    }

    switch (res.Error?.ErrorType) {
      case UPDATE_PASSWORD_ERROR.OldPasswordDoesntMatch:
        return 'Looks like your old password did not match. Please try again.';
      case UPDATE_PASSWORD_ERROR.MatchesRecentPassword:
        return 'The chosen password matches one of your last 4 passwords used for this account. Please select a new password.';
      default:
        return '';
    }
  }

  confirmedValidator(controlName: string, matchingControlName: string): ValidationErrors | null {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];
      if (
        matchingControl.errors &&
        !matchingControl.errors['confirmedValidator']
      ) {
        return null;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({confirmedValidator: true});
        return {confirmedValidator: true};
      } else {
        matchingControl.setErrors(null);
        return null;
      }
    };
  }
}
